// The Add Members pop-up's two lists (Phelipe 2026-10-08), shared by the
// team-card and owner-members controllers: the company's people on the left
// (tick to add), who's on the project on the right (× to take off). Rows never
// carry data-chip-email: the cards collect their chips by that attribute.

export function pickerTexts(popover) {
  const d = popover?.dataset || {}
  return {
    loading: d.loadingText || "Loading people…",
    none: d.noneText || "No one at this company yet.",
    noMatch: d.noMatchText || "No one matches.",
    nobody: d.nobodyText || "No one added yet.",
    auto: d.autoText || "Auto",
    remove: d.removeText || "Remove",
    aaWhy: d.aaWhyText || "Admin Assistant: picked in the Details step. Change it there.",
    newTag: d.newText || "New",
    newTitle: d.newTitleText || "New member",
    autoTitle: d.autoTitleText || "Added automatically",
    aaTitle: d.aaTitleText || "Admin Assistant",
    newWhy: d.newWhyText || "New member: they'll get an email invitation to join ConsLog and this project.",
    invalidEmail: d.invalidEmailText || "Please enter a valid email address.",
    alreadyAdded: d.alreadyAddedText || "This person is already on the list.",
    noSuchEmail: d.noSuchEmailText || "This email address doesn't exist. Please check it for typos.",
    checking: d.checkingText || "Checking…",
    add: d.addText || "Add"
  }
}

// Invited-by-email people, everywhere (pop-up cards and the chips under the
// card): the role's own color, darker (Phelipe 2026-10-09: "same tone as the
// other labels for that role but darker" — dark green on the Builder row).
export function newMemberColor(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ""))
  if (!m) return "#166534"
  const n = parseInt(m[1], 16)
  const dark = (c) => Math.round(c * 0.6).toString(16).padStart(2, "0")
  return `#${dark(n >> 16)}${dark((n >> 8) & 255)}${dark(n & 255)}`
}

// The small "New" tag that rides inside a chip under the card.
export function newChipTag(texts, color) {
  return `<span class="inline-flex items-center rounded-full px-1.5 text-[13px] font-semibold" style="background:${color}24;color:${color};line-height:1.35">${escHtml(texts?.newTag || "New")}</span>`
}

export function escHtml(str) {
  return String(str ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;")
}

function initials(name, email) {
  const words = String(name || email || "?").replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean)
  return ((words[0]?.[0] || "?") + (words.length > 1 ? words[words.length - 1][0] : "")).toUpperCase()
}

// color: a hex for a tinted circle (the role's chip color), or none for gray.
function avatar(name, email, color = null) {
  const gray = color ? "" : " bg-gray-100 text-gray-600"
  const tint = color ? ` style="background:${color}1F;color:${color}"` : ""
  return `<span class="shrink-0 w-8 h-8 rounded-full text-[13px] font-semibold flex items-center justify-center${gray}"${tint}>${escHtml(initials(name, email))}</span>`
}

// A lock: plain text, or { text, why, title } — "why" shows in the hover tip
// (attachWhyTips below), which opens beside the tag and never covers the name.
function badge(lock) {
  const text = typeof lock === "string" ? lock : lock?.text
  const why = typeof lock === "string" ? "" : lock?.why
  const pill = `<span class="rounded bg-gray-100 text-gray-500 px-1.5 py-0.5 text-[13px] ${why ? "cursor-help underline decoration-dotted underline-offset-2" : ""}">${escHtml(text)}</span>`
  if (!why) return `<span class="ml-2 shrink-0">${pill}</span>`
  return `<span class="ml-2 shrink-0" data-why="${escHtml(why)}" data-why-title="${escHtml(lock.title || "")}">${pill}</span>`
}

// Left list. members: [{name, email}]; selected: Set of emails;
// locked: Map email → badge text or { text, why } (can't be unticked).
export function peopleRows({ members, selected, locked = new Map(), query = "", loaded = true, ctrl, texts }) {
  if (!loaded) return `<div class="px-4 py-8 text-[13px] text-gray-400 text-center">${escHtml(texts.loading)}</div>`
  const q = query.trim().toLowerCase()
  const list = q ? members.filter((m) => `${m.name} ${m.email}`.toLowerCase().includes(q)) : members
  if (!list.length) {
    return `<div class="px-4 py-8 text-[13px] text-gray-400 text-center">${escHtml(q ? texts.noMatch : texts.none)}</div>`
  }
  return list.map((m) => {
    const lockText = locked.get(m.email)
    return `<label class="flex items-center gap-3 px-2.5 py-2 rounded-lg ${lockText ? "opacity-70 cursor-default" : "cursor-pointer hover:bg-gray-50"} transition-colors">
      <input type="checkbox" ${selected.has(m.email) ? "checked" : ""} ${lockText ? "disabled" : ""}
             value="${escHtml(m.email)}" data-name="${escHtml(m.name)}"
             data-action="change->${ctrl}#toggleMember" class="form-checkbox ${lockText ? "" : "cursor-pointer"}">
      ${avatar(m.name, m.email)}
      <span class="flex-1 min-w-0">
        <span class="block text-sm font-medium text-gray-900 truncate">${escHtml(m.name || m.email)}</span>
        <span class="block text-[13px] text-gray-500 truncate">${escHtml(m.email)}</span>
      </span>
      ${lockText ? badge(lockText) : ""}
    </label>`
  }).join("")
}

// Right list. entries: [{email, label, lock, isNew}] — lock = badge (text or
// { text, why }) instead of ×; isNew = invited by email, not at the company
// yet (shown in blue with a "New" tag, Phelipe 2026-10-08).
// color: the hex the chips under the card use for this role (green for the
// Builder/Owner block), so a person looks the same in both places.
export function selectedRows({ entries, ctrl, texts, color = "#16a34a" }) {
  if (!entries.length) return `<div class="px-3 py-8 text-[13px] text-gray-400 text-center">${escHtml(texts.nobody)}</div>`
  return entries.map(({ email, label, lock, isNew }) => {
    const showEmail = label && label !== email
    const tone = isNew ? newMemberColor(color) : color
    return `<div class="flex items-center gap-3 px-2.5 py-2 rounded-lg" style="background:${tone}0F;border:1px solid ${tone}4D" ${isNew ? "data-new-member" : ""}>
      ${avatar(label, email, tone)}
      <span class="flex-1 min-w-0">
        <span class="block text-sm font-medium text-gray-900 truncate">${escHtml(label || email)}</span>
        ${showEmail ? `<span class="block text-[13px] text-gray-500 truncate">${escHtml(email)}</span>` : ""}
      </span>
      ${isNew ? `<span class="shrink-0 rounded-full px-2 py-px text-[13px] font-semibold cursor-help" style="background:${tone}24;color:${tone}" data-why="${escHtml(texts.newWhy)}" data-why-title="${escHtml(texts.newTitle)}">${escHtml(texts.newTag)}</span>` : ""}
      ${lock
        ? badge(lock)
        : `<button type="button" data-action="click->${ctrl}#removeChip" data-email="${escHtml(email)}" title="${escHtml(texts.remove)}"
                   class="shrink-0 w-7 h-7 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors">&times;</button>`}
    </div>`
  }).join("")
}

// Checks an email typed into Invite New Member: its shape, that it isn't on
// the list already, then (when the page gives a URL) that the address can
// receive mail. Resolves to an error message, or "" when it's fine.
export async function checkInviteEmail(email, { selected, validateUrl, texts }) {
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return texts.invalidEmail
  if (selected.has(email)) return texts.alreadyAdded
  if (!validateUrl) return ""
  try {
    const resp = await fetch(`${validateUrl}?email=${encodeURIComponent(email)}`, { headers: { Accept: "application/json" } })
    if (resp.ok && (await resp.json()).is_valid === false) return texts.noSuchEmail
  } catch { /* the check is a nicety; never block on it */ }
  return ""
}

// The hover tip for tags carrying data-why (Phelipe 2026-10-08: "a nice
// tooltip … to the right so it's not covering the name"). One tip on <body>,
// fixed, so the scrolling lists and the pop-up's edge never cut it off. It
// opens to the tag's right; when the window has no room there, below it.
export function attachWhyTips(root) {
  if (!root) return { hide() {}, destroy() {} }
  const doc = root.ownerDocument
  let tip = null
  let current = null

  const build = () => {
    tip = doc.createElement("div")
    tip.setAttribute("role", "tooltip")
    tip.className = "fixed z-[120] pointer-events-none max-w-[260px] rounded-lg bg-gray-900 text-white shadow-xl px-3 py-2 text-[13px] leading-snug opacity-0 transition-opacity duration-100"
    tip.innerHTML = `<span data-tip-arrow class="absolute w-2.5 h-2.5 bg-gray-900 rotate-45"></span>
      <span data-tip-title class="block font-semibold"></span><span data-tip-body class="block text-gray-200"></span>`
    doc.body.appendChild(tip)
  }

  const show = (el) => {
    if (!tip) build()
    current = el
    const title = el.dataset.whyTitle || ""
    tip.querySelector("[data-tip-title]").textContent = title
    tip.querySelector("[data-tip-title]").hidden = !title
    tip.querySelector("[data-tip-body]").textContent = el.dataset.why
    tip.style.left = "0px"; tip.style.top = "0px"
    const r = el.getBoundingClientRect()
    const t = tip.getBoundingClientRect()
    const view = doc.defaultView
    const arrow = tip.querySelector("[data-tip-arrow]")
    if (r.right + 10 + t.width <= view.innerWidth - 8) {
      // Beside the tag, to its right.
      tip.style.left = `${r.right + 10}px`
      tip.style.top = `${Math.max(8, r.top + r.height / 2 - t.height / 2)}px`
      arrow.style.cssText = "left:-4px;top:50%;margin-top:-5px"
    } else {
      // No room on the right: under the tag, its right edges lined up.
      tip.style.left = `${Math.max(8, r.right - t.width)}px`
      tip.style.top = `${r.bottom + 8}px`
      arrow.style.cssText = `top:-4px;left:${Math.min(t.width - 16, Math.max(6, r.left + r.width / 2 - Math.max(8, r.right - t.width) - 5))}px`
    }
    tip.classList.replace("opacity-0", "opacity-100")
  }

  const hide = () => {
    current = null
    if (tip) tip.classList.replace("opacity-100", "opacity-0")
  }

  const over = (e) => {
    const el = e.target.closest?.("[data-why]")
    if (el && root.contains(el) && el !== current) show(el)
  }
  const out = (e) => {
    if (!current) return
    if (e.relatedTarget && current.contains(e.relatedTarget)) return
    hide()
  }
  root.addEventListener("mouseover", over)
  root.addEventListener("mouseout", out)
  root.addEventListener("scroll", hide, true)

  return {
    hide,
    destroy() {
      root.removeEventListener("mouseover", over)
      root.removeEventListener("mouseout", out)
      root.removeEventListener("scroll", hide, true)
      tip?.remove()
    }
  }
}
